import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { withCountryPrefix, withQuery } from "../components/utils/routeHelpers";
import { getCapabilities } from "../utils/capabilities";

function money(value, currency = "EUR") {
  const symbol = currency === "USD" ? "$" : "€";
  return `${symbol}${Number(value || 0).toFixed(2)}`;
}

function DeltaChip({ deltaPct, deltaAbs }) {
  if (deltaPct == null && deltaAbs == null) return null;
  return (
    <Chip
      color="success"
      size="small"
      label={`${Math.abs(Number(deltaPct || 0)).toFixed(1)}% · ${money(Math.abs(deltaAbs || 0))}`}
    />
  );
}

function UpgradeBanner({ title, body, ctaLabel, onClick }) {
  return (
    <Alert
      severity="info"
      sx={{
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        background: "linear-gradient(135deg, rgba(255,248,225,0.9), rgba(232,245,233,0.9))",
      }}
      action={
        <Button size="small" variant="contained" onClick={onClick}>
          {ctaLabel}
        </Button>
      }
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2">{body}</Typography>
    </Alert>
  );
}

export default function PriceIntelligencePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { country = "el" } = useParams();
  const navigate = useNavigate();
  const { caps } = useMemo(() => getCapabilities({ user }), [user]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deals, setDeals] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [watchlistMeta, setWatchlistMeta] = useState({});
  const [alertsMeta, setAlertsMeta] = useState({});
  const [alertsPolicy, setAlertsPolicy] = useState({});
  const [basketSavings, setBasketSavings] = useState(null);
  const [formState, setFormState] = useState({});

  const plansPath = withQuery(withCountryPrefix("/plans", country), {
    intent: "premium",
    plan: "monthly",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [dealsRes, savingsRes, watchlistRes, alertsRes] = await Promise.allSettled([
        api.price.getDeals(12),
        api.price.getSmartBasketSavings(),
        caps.priceWatch.available
          ? api.price.getWatchlist()
          : Promise.resolve({ data: { items: [], meta: {}, policy: {} } }),
        user ? api.price.getAlerts() : Promise.resolve({ data: { alerts: [], meta: {}, policy: {} } }),
      ]);

      if (dealsRes.status === "fulfilled") {
        setDeals(Array.isArray(dealsRes.value?.data?.deals) ? dealsRes.value.data.deals : []);
      }

      if (savingsRes.status === "fulfilled") {
        setBasketSavings(savingsRes.value?.data || null);
      }

      if (watchlistRes.status === "fulfilled") {
        setWatchlist(Array.isArray(watchlistRes.value?.data?.items) ? watchlistRes.value.data.items : []);
        setWatchlistMeta(watchlistRes.value?.data?.meta || {});
      }

      if (alertsRes.status === "fulfilled") {
        setAlerts(Array.isArray(alertsRes.value?.data?.alerts) ? alertsRes.value.data.alerts : []);
        setAlertsMeta(alertsRes.value?.data?.meta || {});
        setAlertsPolicy(alertsRes.value?.data?.policy || {});
      } else if (alertsRes.reason?.response?.status !== 403) {
        throw alertsRes.reason;
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load price intelligence."
      );
    } finally {
      setLoading(false);
    }
  }, [caps.priceWatch.available, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const canUseAlerts = useMemo(() => alertsPolicy?.can_use_alerts !== false, [alertsPolicy]);
  const isPremium = useMemo(
    () => caps.priceWatch.available || alertsPolicy?.push_ready === true || user?.entitlements?.tier === "premium",
    [alertsPolicy, caps.priceWatch.available, user]
  );

  const handleWatchlistRemove = async (variantId) => {
    await api.price.updateWatchlist({ variant_id: variantId, action: "remove" });
    loadData();
  };

  const handleAlertSave = async (variantId) => {
    const entry = formState[variantId] || {};
    const payload = {
      variant_id: variantId,
      direction: entry.direction || "below",
      notification_channel: isPremium ? "hybrid" : "email",
    };

    if (entry.mode === "unit_price") payload.target_unit_price = entry.value;
    else payload.target_price = entry.value;

    await api.price.createAlert(payload);
    setFormState((prev) => ({ ...prev, [variantId]: { mode: "price", value: "", direction: "below" } }));
    loadData();
  };

  const handleAlertRemove = async (variantId) => {
    await api.price.createAlert({ variant_id: variantId, action: "remove" });
    loadData();
  };

  const savingsSummary = basketSavings?.summary || {};

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t("price_intelligence_title") || "Price Intelligence"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 720 }}>
          {t("price_intelligence_desc") || "Reduce supermarket costs with live price drops, basket optimization, and meal-planning savings signals."}
        </Typography>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      {!loading && !isPremium ? (
        <Box sx={{ mb: 3 }}>
          <UpgradeBanner
            title={t("price_premium_banner_title") || "Premium unlocks 365-day history and push-ready alerts"}
            body={t("price_premium_banner_body") || "Free accounts can track 30 days and 1 alert. Upgrade for year-long intelligence, unlimited alerts, and mobile-ready delivery."}
            ctaLabel={t("upgrade_to_premium") || "Upgrade"}
            onClick={() => navigate(plansPath)}
          />
        </Box>
      ) : null}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TrendingDownRoundedIcon color="success" />
                <Typography variant="h6">{t("top_price_drops") || "Top Price Drops"}</Typography>
              </Stack>
              <Stack spacing={1.5}>
                {deals.map((deal) => (
                  <Box key={deal.variant_id} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {deal.name || `Variant #${deal.variant_id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(deal.store_chain || deal.store_name || "").trim() || (t("unknown_store") || "Unknown store")}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.5}>
                        <DeltaChip deltaPct={deal.delta_pct} deltaAbs={deal.delta_abs} />
                        <Typography variant="body2">
                          {money(deal.price)} {deal.price_was != null ? `· ${t("was_price") || "was"} ${money(deal.price_was)}` : ""}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
                {!deals.length && !loading ? (
                  <Alert severity="info">{t("no_deals_available") || "No recent price drops available yet."}</Alert>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <SavingsRoundedIcon color="primary" />
                <Typography variant="h6">{t("smart_basket_savings") || "Smart Basket Savings"}</Typography>
              </Stack>
              {savingsSummary?.opportunity_count > 0 ? (
                <>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                    {money(savingsSummary.savings_amount, savingsSummary.currency)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                    {(t("smart_basket_savings_desc") || "Potential savings if you switch to the cheapest available variants in your market basket.")}{" "}
                    {savingsSummary.savings_pct ? `(${Number(savingsSummary.savings_pct).toFixed(1)}%)` : ""}
                  </Typography>
                  <Stack spacing={1}>
                    {(basketSavings?.items || []).slice(0, 4).map((item) => (
                      <Box key={item.item_id || item.generic_product_id} sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {item.current_variant?.name || item.optimized_variant?.name || `Item #${item.generic_product_id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("switch_and_save") || "Switch and save"} {money(item.savings_amount, savingsSummary.currency)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              ) : (
                <Alert severity="info">
                  {t("smart_basket_no_opportunities") || "No basket optimization opportunities found yet. Build a shopping list to compare live alternatives."}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <PlaylistAddCheckRoundedIcon color="warning" />
                <Typography variant="h6">{t("watchlist") || "Watchlist"}</Typography>
                {watchlistMeta?.limit != null ? <Chip size="small" label={`${watchlistMeta.count || 0}/${watchlistMeta.limit}`} /> : null}
              </Stack>
              {!user ? (
                <Alert severity="info">{t("watchlist_requires_login") || "Sign in to build a watchlist and follow supermarket price changes."}</Alert>
              ) : !caps.priceWatch.available ? (
                <UpgradeBanner
                  title={t("premium_feature_title") || "Premium feature"}
                  body={t("price_watch_premium_gate") || "Premium is required for this Price Watch feature."}
                  ctaLabel={t("upgrade_to_premium") || "Upgrade"}
                  onClick={() => navigate(plansPath)}
                />
              ) : watchlist.length === 0 && !loading ? (
                <Alert severity="info">{t("empty_watchlist") || "No watched variants yet. Add items from your shopping list."}</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {watchlist.map((item) => (
                    <Box key={item.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {item.name || `Variant #${item.variant_id}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {money(item.price, item.currency)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DeltaChip deltaPct={item.delta_pct} deltaAbs={item.delta_abs} />
                          <Button size="small" onClick={() => handleWatchlistRemove(item.variant_id)}>
                            {t("remove") || "Remove"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <NotificationsActiveRoundedIcon color="error" />
                <Typography variant="h6">{t("alerts") || "Alerts"}</Typography>
                {alertsMeta?.limit != null ? <Chip size="small" label={`${alertsMeta.count || 0}/${alertsMeta.limit}`} /> : null}
              </Stack>

              {!user ? (
                <Alert severity="info">{t("alerts_require_login") || "Create an account to set price alerts."}</Alert>
              ) : !canUseAlerts ? (
                <UpgradeBanner
                  title={t("alerts_upgrade_title") || "Alerts are not enabled on your plan"}
                  body={t("alerts_upgrade_body") || "Upgrade to unlock more historical context and alert delivery options."}
                  ctaLabel={t("upgrade_to_premium") || "Upgrade"}
                  onClick={() => navigate(plansPath)}
                />
              ) : (
                <>
                  <Stack spacing={1.5}>
                    {watchlist.slice(0, 3).map((item) => (
                      <Box key={`alert-form-${item.id}`} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          {item.name || `Variant #${item.variant_id}`}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <TextField
                            select
                            size="small"
                            label={t("alert_type") || "Alert type"}
                            value={formState[item.variant_id]?.mode || "price"}
                            onChange={(e) =>
                              setFormState((prev) => ({
                                ...prev,
                                [item.variant_id]: { ...(prev[item.variant_id] || {}), mode: e.target.value },
                              }))
                            }
                          >
                            <MenuItem value="price">{t("below_price") || "Below price"}</MenuItem>
                            <MenuItem value="unit_price">{t("below_unit_price") || "Below unit price"}</MenuItem>
                          </TextField>
                          <TextField
                            size="small"
                            label={t("target_value") || "Target value"}
                            value={formState[item.variant_id]?.value || ""}
                            onChange={(e) =>
                              setFormState((prev) => ({
                                ...prev,
                                [item.variant_id]: { ...(prev[item.variant_id] || {}), value: e.target.value, direction: "below" },
                              }))
                            }
                          />
                          <Button variant="contained" onClick={() => handleAlertSave(item.variant_id)}>
                            {t("save_alert") || "Save alert"}
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1}>
                    {alerts.length === 0 ? (
                      <Alert severity="info">{t("no_alerts_yet") || "No active alerts yet."}</Alert>
                    ) : (
                      alerts.map((alertItem) => (
                        <Box key={alertItem.id} sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {alertItem.name || `Variant #${alertItem.variant_id}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {alertItem.target_price != null
                                  ? `${t("target_price") || "Target"}: ${money(alertItem.target_price)}`
                                  : `${t("target_unit_price") || "Unit target"}: ${money(alertItem.target_unit_price)}`}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={alertItem.notification_channel || "email"} />
                              <Button size="small" onClick={() => handleAlertRemove(alertItem.variant_id)}>
                                {t("remove") || "Remove"}
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))
                    )}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
